import { useState, type KeyboardEvent, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  PersonIcon,
  PhoneIcon,
  Stack,
  Text,
  TextField,
} from '@citibox-panel/ui';
import { useAddExcludedUser } from '../hooks/useAddExcludedUser';
import { parseE164Phone } from '../lib/phone';
import type { ExclusionListCarrier, ExclusionListSeams } from '../lib/excludedUser';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface AddUserCardProps extends Pick<ExclusionListSeams, 'addExcludedUser'> {
  carrier: ExclusionListCarrier;
  isHandledByHost?: IsHandledByHost;
}

// TODO: i18n — copy en ES hasta Fase 1.
const FIELD_LABEL = 'Teléfono del usuario';
const EXAMPLE_HINT = 'Ej. +34600111001';
const INVALID_HINT = 'El teléfono va en formato internacional, con + y prefijo';
const UNREGISTERED_HINT = 'No hay ningún «Usuario» registrado con ese teléfono';
// Fixed panel copy: the server's own `message`/`detail` never reaches the DOM.
const WRITE_ERROR = 'No se ha podido añadir al usuario.';

// The add: a phone, a button, and the two answers that are not failures — added, or no live account
// holds that number. No confirmation: the row appears beside it with its own «QUITAR», which is the
// undo. The field empties on success because the new row IS the record, and a field that keeps the
// last phone invites sending it twice.
export function AddUserCard({
  carrier,
  addExcludedUser,
  isHandledByHost = neverHandledByHost,
}: AddUserCardProps): ReactElement {
  const [raw, setRaw] = useState('');
  const { add, isAdding, outcome, isError, error } = useAddExcludedUser(
    carrier.slug,
    addExcludedUser,
    {
      onAdded: () => {
        setRaw('');
      },
    },
  );

  // Trimmed and normalized before it is judged; the rule itself stays strict. An empty field is not
  // mistyped, it is just empty.
  const candidate = parseE164Phone(raw);
  const isInvalid = raw.trim() !== '' && candidate === null;
  // The message follows the phone it answers about, and disappears as soon as that phone changes.
  const isUnregistered =
    outcome?.kind === 'unregistered_phone' && candidate !== null && candidate === outcome.phone;

  // A refusal the host already answers with its own screen gets no alert here.
  const showsWriteError = isError && !isHandledByHost(error);

  const submit = () => {
    if (candidate !== null && !isAdding) add(candidate);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submit();
  };

  const helperText = isInvalid ? INVALID_HINT : isUnregistered ? UNREGISTERED_HINT : EXAMPLE_HINT;

  return (
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <PersonIcon fontSize="small" />
          <Text variant="subtitle2" component="h3" bold>
            Excluir a un usuario
          </Text>
        </Box>
        <TextField
          value={raw}
          onChange={setRaw}
          onKeyDown={handleKeyDown}
          placeholder={FIELD_LABEL}
          // The placeholder is NOT an accessible name: it disappears on the first keystroke.
          aria-label={FIELD_LABEL}
          startAdornment={<PhoneIcon fontSize="small" />}
          helperText={helperText}
          // The tint stays on the frame; the helper keeps the body tone, so the meaning is in the
          // words and never in a colour that measures 3.09:1.
          error={isInvalid || isUnregistered}
          monospace
          fullWidth
        />
        <Stack direction="row" sx={{ mt: 2 }}>
          <Button disabled={candidate === null || isAdding} loading={isAdding} onClick={submit}>
            EXCLUIR
          </Button>
        </Stack>
        {/* Mounted in every state, so the region exists BEFORE its text arrives. */}
        <Box role="status" sx={{ mt: 2 }}>
          {outcome?.kind === 'added' && (
            <Text variant="body2">
              {`${outcome.phone} añadido a la lista de exclusión de ${carrier.name}`}
            </Text>
          )}
        </Box>
        {showsWriteError && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error">{WRITE_ERROR}</Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
